import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Check, X, ChevronDown, Plus, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/lib/context';

// Category icons mapping (normalized keys)
const categoryIcons = {
  power_tools: '⚡',
  hand_tools: '🔧',
  construction: '🏗️',
  garden: '🌱',
  cleaning: '🧹',
  vehicles: '🚗',
  lifting: '🏋️',
  safety: '🦺',
  electrical: '🔌',
  plumbing: '🚿',
  welding: '🔥',
  painting: '🎨',
  demolition: '🔨',
  woodworking: '🪚',
  measuring: '📐',
  concrete: '🧱',
  hvac: '❄️',
  scaffolding: '🪜',
  other: '📦'
};

/**
 * CategoryCombobox - True input-based searchable autocomplete with custom category support
 * 
 * Features:
 * - Type freely in the input field
 * - Real-time filtering of categories as you type
 * - Select from existing categories or enter custom value
 * - All display labels come from i18n (normalized keys)
 * 
 * Data handling:
 * - Selecting existing category → stores category_key (e.g., "power_tools")
 * - Entering custom value → stores category_key="other" and custom_category="{input}"
 */
export const CategoryCombobox = ({
  categories = [],
  value,
  onValueChange,
  customValue = '',
  onCustomValueChange,
  placeholder,
  className,
  disabled = false,
  'data-testid': testId = 'category-combobox'
}) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const containerRef = useRef(null);
  const lastSetValueRef = useRef(null); // Track last manually set value to prevent useEffect override

  // Get localized category name from i18n
  const getCategoryLabel = useCallback((categoryId) => {
    // Try rental-specific translation first, then fallback to general categories
    const rentalKey = `rentalCategories.${categoryId}`;
    const generalKey = `categories.${categoryId}`;
    const rentalLabel = t(rentalKey);
    const generalLabel = t(generalKey);
    
    // If translation returns the key itself, it's not found
    if (rentalLabel !== rentalKey) return rentalLabel;
    if (generalLabel !== generalKey) return generalLabel;
    
    // Fallback to category name from API (for backwards compatibility)
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  }, [t, categories]);

  // Filter categories based on search (exclude "other" - shown separately)
  const filteredCategories = useMemo(() => {
    const baseCategories = categories.filter(cat => cat.id !== 'other');
    if (!inputValue.trim()) return baseCategories;
    
    const query = inputValue.toLowerCase().trim();
    return baseCategories.filter(cat => {
      const label = getCategoryLabel(cat.id).toLowerCase();
      const id = cat.id.toLowerCase();
      return label.includes(query) || id.includes(query);
    });
  }, [categories, inputValue, getCategoryLabel]);

  // Check if input matches any existing category exactly
  const exactMatch = useMemo(() => {
    if (!inputValue.trim()) return null;
    const query = inputValue.toLowerCase().trim();
    return filteredCategories.find(cat => 
      getCategoryLabel(cat.id).toLowerCase() === query
    );
  }, [filteredCategories, inputValue, getCategoryLabel]);

  // Show "use custom" option - always show when typing, with different text
  const showCustomOption = useMemo(() => {
    return inputValue.trim().length > 0;
  }, [inputValue]);

  // Check if we should show "Other / Write your own" option (always visible)
  const showOtherOption = useMemo(() => {
    // Always show if no input, or if input doesn't exactly match
    return !inputValue.trim() || !exactMatch;
  }, [inputValue, exactMatch]);

  // Combined options: filtered categories + "Other" option
  const allOptions = useMemo(() => {
    const options = [...filteredCategories];
    
    // Always add "Other / Write your own" option at the end
    if (showOtherOption) {
      if (showCustomOption && !exactMatch) {
        // User is typing something that doesn't match - show as "Use custom: {text}"
        options.push({ id: '__custom__', isCustom: true, customText: inputValue.trim() });
      } else if (!inputValue.trim()) {
        // No input - show generic "Other / Write your own"
        options.push({ id: '__other__', isOther: true });
      }
    }
    return options;
  }, [filteredCategories, showOtherOption, showCustomOption, inputValue, exactMatch]);

  // Initialize input value from current selection
  // Skip if we just manually set a value that matches current props
  useEffect(() => {
    // If the current input matches what we just set, don't override
    if (lastSetValueRef.current !== null && inputValue === lastSetValueRef.current) {
      lastSetValueRef.current = null; // Clear after checking
      return;
    }
    
    if (value === 'other' && customValue) {
      setInputValue(customValue);
    } else if (value && value !== 'other') {
      setInputValue(getCategoryLabel(value));
    } else if (!value) {
      setInputValue('');
    }
  }, [value, customValue, getCategoryLabel]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < allOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : allOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (allOptions[highlightedIndex]) {
          handleSelect(allOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };

  // Handle selection
  const handleSelect = (option) => {
    if (option.isCustom) {
      // Custom value selected
      const customText = option.customText;
      lastSetValueRef.current = customText; // Track what we're setting
      onValueChange('other');
      onCustomValueChange?.(customText);
      setInputValue(customText);
    } else {
      // Existing category selected
      const label = getCategoryLabel(option.id);
      lastSetValueRef.current = label; // Track what we're setting
      onValueChange(option.id);
      onCustomValueChange?.('');
      setInputValue(label);
    }
    setIsOpen(false);
    setHighlightedIndex(0);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(0);
    
    // If clearing input, clear the selection
    if (!newValue.trim()) {
      onValueChange('');
      onCustomValueChange?.('');
    }
  };

  // Handle focus
  const handleFocus = () => {
    setIsOpen(true);
  };

  // Clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    setInputValue('');
    onValueChange('');
    onCustomValueChange?.('');
    inputRef.current?.focus();
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const placeholderText = placeholder || t('rentals.createEquipment.categoryPlaceholder');

  return (
    <div 
      ref={containerRef} 
      className={cn('relative', className)}
      data-testid={testId}
    >
      {/* Input field */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholderText}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-20 py-2 text-sm",
            "ring-offset-background placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            value && "pr-20"
          )}
          data-testid={`${testId}-input`}
          autoComplete="off"
        />
        
        {/* Right side icons */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {/* Show selected category icon */}
          {value && value !== 'other' && (
            <span className="text-base mr-1">{categoryIcons[value] || '📦'}</span>
          )}
          {value === 'other' && (
            <span className="text-base mr-1">📦</span>
          )}
          
          {/* Clear button */}
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-muted rounded-sm"
              data-testid={`${testId}-clear`}
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
          
          {/* Dropdown indicator */}
          <ChevronDown className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            isOpen && "transform rotate-180"
          )} />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg"
          data-testid={`${testId}-dropdown`}
        >
          <ul 
            ref={listRef}
            className="max-h-60 overflow-auto py-1"
            role="listbox"
          >
            {allOptions.length === 0 ? (
              <li className="px-3 py-2 text-sm text-muted-foreground text-center">
                {t('rentals.createEquipment.noCategoriesFound')}
              </li>
            ) : (
              allOptions.map((option, index) => {
                // "Other / Write your own" option (when no input)
                if (option.isOther) {
                  return (
                    <li
                      key="__other__"
                      role="option"
                      aria-selected={highlightedIndex === index}
                      onClick={() => {
                        // Focus input for user to type custom value
                        inputRef.current?.focus();
                        setHighlightedIndex(index);
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer",
                        "border-t border-dashed",
                        highlightedIndex === index && "bg-accent text-accent-foreground"
                      )}
                      data-testid={`${testId}-option-other`}
                    >
                      <Plus className="h-4 w-4 text-primary" />
                      <span className="text-base">{categoryIcons.other}</span>
                      <span className="flex-1">
                        <span className="font-medium">{t('rentals.createEquipment.otherWriteYourOwn')}</span>
                      </span>
                    </li>
                  );
                }
                
                // Custom value option (when user types non-matching text)
                if (option.isCustom) {
                  return (
                    <li
                      key="__custom__"
                      role="option"
                      aria-selected={highlightedIndex === index}
                      onClick={() => handleSelect(option)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer",
                        "border-t border-dashed",
                        highlightedIndex === index && "bg-accent text-accent-foreground"
                      )}
                      data-testid={`${testId}-option-custom`}
                    >
                      <Plus className="h-4 w-4 text-primary" />
                      <span className="text-base">{categoryIcons.other}</span>
                      <span className="flex-1">
                        <span className="font-medium">{t('rentals.createEquipment.useCustomValue')}</span>
                        <span className="text-muted-foreground ml-1">"{option.customText}"</span>
                      </span>
                    </li>
                  );
                }

                // Regular category option
                const isSelected = value === option.id;
                const label = getCategoryLabel(option.id);
                const icon = categoryIcons[option.id] || '📦';

                return (
                  <li
                    key={option.id}
                    role="option"
                    aria-selected={highlightedIndex === index}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer",
                      highlightedIndex === index && "bg-accent text-accent-foreground",
                      isSelected && "bg-primary/10"
                    )}
                    data-testid={`${testId}-option-${option.id}`}
                  >
                    {isSelected ? (
                      <Check className="h-4 w-4 text-primary" />
                    ) : (
                      <div className="w-4" />
                    )}
                    <span className="text-base">{icon}</span>
                    <span className="flex-1">{label}</span>
                  </li>
                );
              })
            )}
          </ul>
          
          {/* Footer hint */}
          {inputValue.trim() && !exactMatch && (
            <div className="border-t px-3 py-2 text-xs text-muted-foreground bg-muted/50">
              {t('rentals.createEquipment.customCategoryHint')}
            </div>
          )}
        </div>
      )}

      {/* Selected custom category indicator */}
      {value === 'other' && customValue && (
        <p className="mt-1 text-xs text-muted-foreground">
          {t('rentals.createEquipment.usingCustomCategory')}: <span className="font-medium">{customValue}</span>
        </p>
      )}
    </div>
  );
};

export default CategoryCombobox;
